import React, { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "../../../../Components/Others/ToastServices";
import axiosInstance from "../../../../Services/axiosCommon";

// ================= Preview Component =================
function ThemePreview({ theme }) {
  return (
    <div
      className="w-full rounded-2xl shadow-2xl p-6 transition-all backdrop-blur-md bg-white/70 border border-white/30"
      style={{
        backgroundColor: theme.primary_color
          ? `${theme.primary_color}20`
          : "#ffffff70",
        color: theme.secondary_color || "#2d3748",
      }}
    >
      <h2 className="text-2xl font-bold mb-3">Theme Preview</h2>
      <p className="mb-4">
        This is how your app will look with the selected theme.
      </p>

      {/* Buttons */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <button
          className="py-2 px-4 rounded-xl font-medium transition-all hover:scale-105"
          style={{
            backgroundColor: theme.primary_color || "#4f46e5",
            color: theme.secondary_color || "#ffffff",
          }}
        >
          Primary Button
        </button>
        <button
          className="py-2 px-4 rounded-xl font-medium border transition-all hover:scale-105"
          style={{
            borderColor: theme.primary_color || "#4f46e5",
            color: theme.primary_color || "#4f46e5",
            backgroundColor: "transparent",
          }}
        >
          Secondary Button
        </button>
      </div>

      {/* Card */}
      <div className="bg-white/50 p-4 rounded-xl mb-4">
        <h3
          className="font-semibold mb-2"
          style={{ color: theme.primary_color || "#4f46e5" }}
        >
          Content Card
        </h3>
        <p className="text-sm">
          This card demonstrates how content might appear with your theme.
        </p>
      </div>

      {/* Palette */}
      <div className="flex gap-3 mt-4 flex-wrap">
        {Array.isArray(theme.color) &&
          theme.color.map((c, i) => (
            <div
              key={i}
              className="w-10 h-10 rounded-xl shadow-md transition-transform hover:scale-110"
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
      </div>
    </div>
  );
}

// ================= Palette Generator =================
function PaletteGenerator({ onAddColor }) {
  const [currentColor, setCurrentColor] = useState("#4f46e5");

  const generateHarmoniousPalette = () => {
    const baseColor = currentColor.replace("#", "");
    const r = parseInt(baseColor.substr(0, 2), 16);
    const g = parseInt(baseColor.substr(2, 2), 16);
    const b = parseInt(baseColor.substr(4, 2), 16);

    const colors = [];
    for (let i = 0; i < 5; i++) {
      const newR = Math.min(255, r + i * 30)
        .toString(16)
        .padStart(2, "0");
      const newG = Math.min(255, g + i * 20)
        .toString(16)
        .padStart(2, "0");
      const newB = Math.min(255, b + i * 10)
        .toString(16)
        .padStart(2, "0");
      colors.push(`#${newR}${newG}${newB}`);
    }
    return colors;
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-5 rounded-2xl shadow-lg border border-white/30">
      <h3 className="font-bold text-lg mb-3">Color Palette Generator</h3>

      <div className="flex items-center gap-3 mb-4">
        <input
          type="color"
          value={currentColor}
          onChange={(e) => setCurrentColor(e.target.value)}
          className="w-12 h-12 rounded-lg cursor-pointer"
        />
        <div className="flex-1">
          <p className="text-sm font-medium mb-1">Base Color: {currentColor}</p>
          <input
            type="text"
            value={currentColor}
            onChange={(e) => setCurrentColor(e.target.value)}
            className="w-full p-2 rounded-lg border border-gray-200 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => onAddColor(currentColor)}
          className="bg-white border border-purple-200 text-purple-700 py-2 px-4 rounded-lg font-medium transition-all hover:bg-purple-50 hover:shadow-md"
        >
          Add This Color
        </button>

        <button
          onClick={() => {
            generateHarmoniousPalette().forEach((c) => onAddColor(c));
          }}
          className="bg-gradient-to-r from-purple-500 to-blue-500 text-white py-2 px-4 rounded-lg font-medium transition-all hover:shadow-lg hover:from-purple-600 hover:to-blue-600"
        >
          Generate Palette
        </button>
      </div>

      <div className="mt-4 p-3 bg-white/80 rounded-lg">
        <p className="text-xs text-gray-600 text-center">
          Tip: Start with a base color to generate a harmonious palette
        </p>
      </div>
    </div>
  );
}

// ================= Main Component =================
export default function IndexTheme() {
  const [themes, setThemes] = useState([]);
  const [currentTheme, setCurrentTheme] = useState({
    name: "",
    primary_color: "#4f46e5",
    secondary_color: "#ffffff",
    color: [],
    is_active: true,
  });

  const [preview, setPreview] = useState(false);
  const [activeTab, setActiveTab] = useState("create");
  const toast = useToast();

  // Fetch all themes
  useEffect(() => {
    axiosInstance
      .get("/themes/get-all")
      .then((res) => {
        const normalizeTheme = (t) => ({
          ...t,
          color: Array.isArray(t.color) ? t.color : [],
        });
        const response = res.data;
        setThemes(Array.isArray(response) ? response.map(normalizeTheme) : []);
      })
      .catch((err) => {
        console.error("Error fetching themes:", err);
        setThemes([]);
      });
  }, []);

  // Add color
  const handleColorAdd = (hex) => {
    setCurrentTheme((prev) => ({
      ...prev,
      color: Array.isArray(prev.color) ? [...prev.color, hex] : [hex],
    }));
  };

  // Remove color
  const handleColorRemove = (i) => {
    setCurrentTheme((prev) => ({
      ...prev,
      color: prev.color.filter((_, idx) => idx !== i),
    }));
  };

  // Submit
  // Submit
const handleSubmit = () => {
  axiosInstance
    .post("/themes/add-theme", currentTheme)
    .then((res) => {
      if (res.data?.status) {
        toast.open({
          type: "success",
          message: "Theme Added",
          description: res.data?.msg || "Theme added successfully!",
        });
        setPreview(false);
        setCurrentTheme({
          name: "",
          primary_color: "#4f46e5",
          secondary_color: "#ffffff",
          color: [],
          is_active: true,
        });
      } else {
        toast.open({
          type: "error",
          message: "Failed",
          description: res.data?.msg || "Theme could not be added",
        });
      }
    })
    .catch((err) => {
      toast.open({
        type: "error",
        message: "Error",
        description: err.response?.data?.msg || "Something went wrong",
      });
      console.error(err);
    });
};



  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="text-center mb-10">
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600 mb-3">
            Theme
          </h1>
          {/* <p className="text-gray-600 max-w-2xl mx-auto">
            Create beautiful, harmonious color themes for your application with
            our intuitive designer tool.
          </p> */}
        </header>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 justify-center">
          {["create", "gallery"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                activeTab === tab
                  ? "bg-white text-purple-700 shadow-lg"
                  : "bg-white/50 text-gray-600 hover:bg-white/80"
              }`}
            >
              {tab === "create" ? "Create Theme" : "Theme Gallery"}
            </button>
          ))}
        </div>

        {/* Create Tab */}
        {activeTab === "create" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {/* Form */}
            <div className="bg-white rounded-3xl shadow-2xl p-6 border border-white/30 backdrop-blur-sm bg-white/80">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">
                Create New Theme
              </h2>

              <div className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Theme Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter a creative name"
                    className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    value={currentTheme.name}
                    onChange={(e) =>
                      setCurrentTheme({ ...currentTheme, name: e.target.value })
                    }
                  />
                </div>

                {/* Primary / Secondary */}
                <div className="grid grid-cols-2 gap-4">
                  {["primary_color", "secondary_color"].map((key) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {key === "primary_color"
                          ? "Primary Color"
                          : "Secondary Color"}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentTheme[key]}
                          onChange={(e) =>
                            setCurrentTheme({
                              ...currentTheme,
                              [key]: e.target.value,
                            })
                          }
                          className="w-10 h-10 rounded-lg cursor-pointer"
                        />
                        <span className="text-sm font-mono">
                          {currentTheme[key]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Palette */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Palette
                  </label>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {currentTheme.color.length > 0 ? (
                      currentTheme.color.map((c, i) => (
                        <div key={i} className="relative group">
                          <div
                            className="w-8 h-8 rounded-lg shadow-md cursor-pointer transition-transform group-hover:scale-110"
                            style={{ backgroundColor: c }}
                          />
                          <button
                            onClick={() => handleColorRemove(i)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        No colors added yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Generator */}
                <PaletteGenerator onAddColor={handleColorAdd} />

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all hover:from-purple-700 hover:to-blue-700"
                    onClick={handleSubmit}
                  >
                    Save Theme
                  </button>
                  <button
                    className="bg-white text-gray-700 border border-gray-200 px-6 py-3 rounded-xl font-medium shadow-sm hover:shadow-md transition-all"
                    onClick={() => setPreview(!preview)}
                  >
                    {preview ? "Hide Preview" : "Show Preview"}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="flex flex-col">
              {preview ? (
                <ThemePreview theme={currentTheme} />
              ) : (
                <div className="h-full flex items-center justify-center bg-white/30 rounded-3xl border border-white/30 backdrop-blur-sm">
                  <div className="text-center p-6 text-gray-500">
                    <div className="mb-4 text-5xl">🎨</div>
                    <h3 className="font-medium mb-2">Theme Preview</h3>
                    <p className="text-sm">
                      Click "Show Preview" to see your theme in action
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <div className="bg-white rounded-3xl shadow-2xl p-6 border border-white/30 backdrop-blur-sm bg-white/80 mb-10">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              Theme Gallery
            </h2>

            {themes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {themes.map((theme, i) => (
                  <div
                    key={i}
                    className="rounded-2xl p-5 shadow-lg cursor-pointer transition-transform hover:scale-105 border border-white/30 backdrop-blur-sm"
                    style={{
                      backgroundColor: theme.primary_color
                        ? `${theme.primary_color}15`
                        : "#f8fafc",
                      backgroundImage: `linear-gradient(to bottom right, ${theme.primary_color}10, ${theme.secondary_color}10)`,
                    }}
                    onClick={() => {
                      setCurrentTheme(theme);
                      setActiveTab("create");
                    }}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3
                        className="font-bold text-lg"
                        style={{
                          color: theme.primary_color || "#4f46e5",
                        }}
                      >
                        {theme.name}
                      </h3>
                      {theme.is_active && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          Active
                        </span>
                      )}
                    </div>

                    <div className="flex gap-1 mb-4">
                      <div
                        className="w-8 h-8 rounded-lg shadow-inner"
                        style={{
                          backgroundColor: theme.primary_color || "#4f46e5",
                        }}
                      />
                      <div
                        className="w-8 h-8 rounded-lg shadow-inner border border-gray-200"
                        style={{
                          backgroundColor: theme.secondary_color || "#ffffff",
                        }}
                      />
                    </div>

                    <div className="flex gap-1 flex-wrap">
                      {theme.color?.slice(0, 5).map((c, j) => (
                        <div
                          key={j}
                          className="w-6 h-6 rounded-md shadow-sm"
                          style={{ backgroundColor: c }}
                        />
                      ))}
                      {theme.color && theme.color.length > 5 && (
                        <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                          +{theme.color.length - 5}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="text-5xl mb-4">🎨</div>
                <h3 className="font-medium text-gray-700 mb-2">No themes yet</h3>
                <p className="text-gray-500">
                  Create your first theme to get started
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
